import { MessagingPlatform } from '../messagingPlatform.enum';

interface LoadChatCommunicationsBase {
  timeRangeMinutes: number;
  limit: number;
  useCursor?: boolean;
  contextMessageCount?: number;
  conversationContext?: boolean;
}

/**
 * Flat union, not `{ platform, target: {...} }` — apps/api/src/utils/flowStepCrypto.ts's
 * splitStepConfig only inspects top-level stepConfig keys, so nesting identity
 * fields (phoneNumberId included) would skip FLOW_STEP_CONTENT_KEYS sealing.
 */
export type LoadChatCommunicationsStepConfig =
  | (LoadChatCommunicationsBase & {
      platform: MessagingPlatform.SLACK;
      workspaceId: string;
      channelId: string;
    })
  | (LoadChatCommunicationsBase & {
      platform: MessagingPlatform.TEAMS;
      chatId: string;
    })
  | (LoadChatCommunicationsBase & {
      platform: MessagingPlatform.GOOGLE_CHAT;
      emailAddress: string;
      spaceName: string;
    })
  | (LoadChatCommunicationsBase & {
      platform: MessagingPlatform.INSTAGRAM;
      igAccountId: string;
      conversationId: string;
    })
  | (LoadChatCommunicationsBase & {
      platform: MessagingPlatform.WHATSAPP;
      phoneNumberId: string;
    })
  | (LoadChatCommunicationsBase & {
      platform: MessagingPlatform.MESSENGER;
      pageId: string;
      conversationId: string;
    });

export type LoadChatPlatform = LoadChatCommunicationsStepConfig['platform'];

type LoadChatBaseKey = keyof LoadChatCommunicationsBase;

export type LoadChatIdentityField<P extends LoadChatPlatform> = Exclude<
  keyof Extract<LoadChatCommunicationsStepConfig, { platform: P }>,
  LoadChatBaseKey | 'platform'
>;

export type LoadChatIdentityTable = {
  readonly [P in LoadChatPlatform]: readonly LoadChatIdentityField<P>[];
};

type CompleteIdentityTable<T extends LoadChatIdentityTable> = {
  readonly [P in LoadChatPlatform]: [LoadChatIdentityField<P>] extends [
    T[P][number],
  ]
    ? T[P]
    : never;
};

const defineIdentityTable = <const T extends LoadChatIdentityTable>(
  table: T & CompleteIdentityTable<T>,
): LoadChatIdentityTable => table;

export const LOAD_CHAT_COMMUNICATIONS_IDENTITY_FIELDS = defineIdentityTable({
  [MessagingPlatform.SLACK]: ['workspaceId', 'channelId'],
  [MessagingPlatform.TEAMS]: ['chatId'],
  [MessagingPlatform.GOOGLE_CHAT]: ['emailAddress', 'spaceName'],
  [MessagingPlatform.INSTAGRAM]: ['igAccountId', 'conversationId'],
  [MessagingPlatform.WHATSAPP]: ['phoneNumberId'],
  [MessagingPlatform.MESSENGER]: ['pageId', 'conversationId'],
});
